import { IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Sheet, useToast } from '@/components/common';
import { useCombobox } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import type { IcdRef, Patient } from '@/types/api';
import { ALLERGY_KEYS, allergyLabel, isAllergyKey } from '@/utils/allergies';
import { patientsApi } from '../../patients/api';

type Props = {
  patient: Patient;
  onClose: () => void;
  onSaved: (patient: Patient) => void;
};

/** ICD-10 search as typed (code or name), with the clinic's most used codes when empty. */
function useIcdResults(query: string): IcdRef[] {
  const [frequent, setFrequent] = useState<IcdRef[]>([]);
  const [found, setFound] = useState<IcdRef[]>([]);
  useEffect(() => {
    patientsApi
      .frequentIcd()
      .then(setFrequent)
      .catch(() => setFrequent([]));
  }, []);
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setFound([]);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      patientsApi
        .searchIcd(q)
        .then((list) => live && setFound(list))
        .catch(() => live && setFound([]));
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);
  return query.trim() ? found : frequent;
}

function Allergies({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const [other, setOther] = useState('');
  const toggle = (a: string) =>
    onChange(value.includes(a) ? value.filter((x) => x !== a) : [...value, a]);
  const add = (e: FormEvent) => {
    e.preventDefault();
    const text = other.trim();
    if (text && !value.includes(text)) onChange([...value, text]);
    setOther('');
  };
  return (
    <div className="field">
      <span className="field__label">{t('chart.health.allergies')}</span>
      <div className="chips">
        {ALLERGY_KEYS.map((a) => (
          <Chip key={a} pressed={value.includes(a)} onClick={() => toggle(a)}>
            {allergyLabel(a)}
          </Chip>
        ))}
        {value
          .filter((a) => !isAllergyKey(a))
          .map((a) => (
            <Chip key={a} pressed onClick={() => toggle(a)}>
              {a} <IconX size={14} />
            </Chip>
          ))}
      </div>
      <form className="row" onSubmit={add} style={{ flexWrap: 'nowrap' }}>
        <input
          className="input"
          value={other}
          maxLength={100}
          placeholder={t('chart.health.otherAllergy')}
          aria-label={t('chart.health.otherAllergy')}
          onChange={(e) => setOther(e.target.value)}
        />
        <button type="submit" className="btn" disabled={!other.trim()}>
          <IconPlus size={18} /> {t('chart.health.add')}
        </button>
      </form>
    </div>
  );
}

function Diagnoses({ value, onChange }: { value: IcdRef[]; onChange: (v: IcdRef[]) => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const results = useIcdResults(query).filter((r) => !value.some((v) => v.code === r.code));
  // No onClose: the list is always there, and Escape still closes the sheet.
  const combo = useCombobox({
    id: 'icd-results',
    items: results,
    onPick: (r) => onChange([...value, r]),
  });
  return (
    <div className="field">
      <span className="field__label">{t('chart.health.diagnoses')}</span>
      {value.length > 0 && (
        <ul className="icd-list">
          {value.map((d) => (
            <li key={d.code} className="icd-list__item">
              <span className="icd-code">{d.code}</span>
              <span className="grow">{d.name}</span>
              <button
                type="button"
                className="icon-btn icon-btn--sm"
                onClick={() => onChange(value.filter((x) => x.code !== d.code))}
                aria-label={t('chart.health.remove', { name: d.code })}
              >
                <IconX size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="search">
        <IconSearch size={20} />
        <input
          className="input"
          type="search"
          value={query}
          placeholder={t('chart.health.searchIcd')}
          aria-label={t('chart.health.searchIcd')}
          {...combo.input}
          onChange={(e) => {
            setQuery(e.target.value);
            combo.reset();
          }}
        />
      </div>
      {query.trim() ? (
        <span className="small muted">{t('chart.health.dentalFirst')}</span>
      ) : (
        results.length > 0 && <span className="small muted">{t('chart.health.frequent')}</span>
      )}
      {results.length > 0 && (
        <div
          {...combo.list}
          role="listbox"
          className="icd-results"
          aria-label={t('chart.health.diagnoses')}
        >
          {results.map((r, i) => (
            <div
              key={r.code}
              {...combo.option(i, r)}
              className={`autocomplete__option ${i === combo.active ? 'is-active' : ''}`}
            >
              <span className="icd-code">{r.code}</span>
              <span>{r.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Diseases (ICD-10) and allergies of the patient: shown at the top of the chart. */
export function HealthSheet({ patient, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [allergies, setAllergies] = useState(patient.allergies);
  const [diagnoses, setDiagnoses] = useState(patient.diagnoses);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await patientsApi.update(patient.id, {
        allergies,
        diagnoses: diagnoses.map((d) => d.code),
      });
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('chart.health.title')} onClose={onClose}>
      <div className="stack">
        <Allergies value={allergies} onChange={setAllergies} />
        <Diagnoses value={diagnoses} onChange={setDiagnoses} />
        <button type="button" className="btn btn--dark btn--block" disabled={busy} onClick={save}>
          {t('common.save')}
        </button>
      </div>
    </Sheet>
  );
}
